import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { fallbackProjects, adaptProjects } from "@/data/projectsAdapter";
import { useLang } from "@/context/LanguageContext";
import NotFound from "@/pages/NotFound";
import { ArrowLeft, ArrowUpRight, PlayCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const asText = (v, lang) => (typeof v === "string" ? v : v?.[lang]);
const asList = (v, lang) => {
  if (Array.isArray(v)) return v;
  const s = asText(v, lang);
  return s ? s.split(" · ").map((x) => x.trim()) : [];
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [projects, setProjects] = useState(fallbackProjects);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/portfolio/projects`)
      .then(({ data }) => {
        if (!mounted) return;
        const adapted = adaptProjects(data);
        if (adapted.length > 0) setProjects(adapted);
      })
      .catch(() => { /* keep fallback */ })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const idx = projects.findIndex((p) => p.slug === slug);
  if (idx === -1) {
    if (loading) return <div className="min-h-screen bg-[#0A0A0C]" />;
    // Real 404 page (with noindex) — better SEO signal than a client-side redirect.
    return <NotFound />;
  }
  const p = projects[idx];
  const next = projects[(idx + 1) % projects.length];

  const approachHeadline = p.approachHeadline
    ? asText(p.approachHeadline, lang)
    : lang === "ar"
      ? "الفكرة · الأسلوب البصري · التقنيات"
      : "Concept · Visual style · Techniques";

  return (
    <div data-testid="project-detail-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40">
        <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
          <img src={p.image} alt={p.title[lang]} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
        </div>
        <div className="container-x -mt-24 md:-mt-32 relative">
          <Link to="/work" className="inline-flex items-center gap-2 text-white/80 hover:text-[#E6A95B] mb-6 text-sm font-medium" data-testid="back-to-work">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("project.back")}
          </Link>
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.24em] bg-[#E6A95B] text-[#0A0A0C] font-bold">
              {t(`cats.${p.category}`)}
            </span>
            <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.24em] border border-white/15 text-white/70">
              {p.year}
            </span>
          </div>
          <h1 className="display-h1 max-w-5xl">{p.title[lang]}</h1>
          <p className="mt-6 text-white/70 text-xl max-w-3xl">{p.short[lang]}</p>
        </div>
      </section>

      {/* VISION 2030 CINEMATIC BLOCK — bold golden title + epic body + full-bleed video */}
      {p.vision && (
        <>
          <section className="relative py-24 md:py-32 overflow-hidden" data-testid="vision-block">
            {/* Cinematic gradient washes */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-40 -start-40 w-[36rem] h-[36rem] rounded-full blur-[140px] opacity-30 bg-[#E6A95B]" />
              <div className="absolute -bottom-40 -end-40 w-[36rem] h-[36rem] rounded-full blur-[140px] opacity-20 bg-[#7a4d1e]" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(10,10,12,0.6)_65%,_#0A0A0C_100%)]" />
              {/* film grain */}
              <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
            </div>

            <div className="relative container-x max-w-5xl">
              <h2
                data-testid="vision-title"
                className="font-display font-black leading-[1.15] tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-[#F6D28C] via-[#E6A95B] to-[#B7822F] text-4xl sm:text-5xl md:text-6xl lg:text-7xl drop-shadow-[0_2px_30px_rgba(230,169,91,0.35)]"
              >
                {asText(p.vision.title, lang)}
              </h2>

              <div className="mt-12 space-y-7 max-w-4xl">
                {(p.vision.paragraphs?.[lang] || []).map((para, i) => (
                  <p
                    key={i}
                    data-testid={`vision-p-${i + 1}`}
                    className="text-white/85 text-lg md:text-2xl leading-[1.9] first:text-xl first:md:text-3xl first:font-medium first:text-white"
                  >
                    {para}
                  </p>
                ))}
              </div>
            </div>
          </section>

          {/* FULL-BLEED VIDEO */}
          {p.video && (
            <section className="relative overflow-hidden" data-testid="vision-fullwidth-video">
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C] via-[#0A0A0C] to-[#0A0A0C]" />
              <div className="relative w-screen aspect-video max-h-[92vh] bg-black">
                <div className="absolute inset-0 pointer-events-none z-10 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent opacity-90" />
                <iframe
                  data-testid="project-video"
                  src={p.video}
                  title={p.title[lang]}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </section>
          )}
        </>
      )}

      {/* META (hidden for projects flagged hideMeta) */}
      {!p.hideMeta && (
        <section className="py-16 md:py-20">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-white/10 py-10">
            <Meta label={t("project.client")} value={asText(p.client, lang)} />
            <Meta label={t("project.timeline")} value={asText(p.timeline, lang)} />
            <Meta label={t("project.locations")} value={asText(p.locations, lang)} />
            <Meta label={t("project.team")} value={asText(p.team, lang)} />
          </div>
        </section>
      )}

      {/* OVERVIEW + APPROACH */}
      {(!p.hideMeta || !p.hideApproach) && (
        <section className={p.hideMeta ? "pt-16 md:pt-20 pb-8" : "pb-8"}>
          <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14">
            {!p.hideMeta && p.goal && (
              <div className="lg:col-span-6">
                <div className="overline mb-4">{t("project.overview")}</div>
                <h2 className="display-h3">{t("project.goal")}</h2>
                <p className="mt-6 text-white/70 text-lg leading-relaxed">{asText(p.goal, lang)}</p>
              </div>
            )}
            {!p.hideApproach && p.approach && (
              <div className={p.hideMeta || !p.goal ? "lg:col-span-12" : "lg:col-span-6"}>
                <div className="overline mb-4">{t("project.approach")}</div>
                <h2 className="display-h3 italic">{approachHeadline}</h2>
                <p className="mt-6 text-white/70 text-lg leading-relaxed">{asText(p.approach, lang)}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* VIDEO (skipped when the vision block already rendered a full-bleed player) */}
      {p.video && !p.vision ? (
        <section className="py-16 md:py-24">
          <div className="container-x">
            <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d10] group">
              <iframe
                data-testid="project-video"
                src={p.video}
                title={p.title[lang]}
                className="absolute inset-0 w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="mt-4 text-center text-white/40 text-xs uppercase tracking-[0.24em] inline-flex items-center gap-2 w-full justify-center">
              <PlayCircle className="w-4 h-4" /> {t("project.watch")}
            </p>
          </div>
        </section>
      ) : null}

      {/* Body descriptor (used for Turning Forward + similar) */}
      {p.body && (
        <section className="pb-16 md:pb-24">
          <div className="container-x max-w-4xl">
            <p className="text-white/75 text-xl md:text-2xl leading-relaxed font-display italic" data-testid="project-body">
              {asText(p.body, lang)}
            </p>
          </div>
        </section>
      )}

      {/* HIGHLIGHTS (hidden if hideHighlights) */}
      {!p.hideHighlights && (
        <section className="py-16 md:py-24 bg-[#0d0d10] border-y border-white/5">
          <div className="container-x">
            <div className="overline mb-4">{t("project.highlights")}</div>
            <h2 className="display-h2">Production Highlights</h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <HighlightCard label={t("project.equipment")} items={asList(p.equipment, lang)} />
              <HighlightCard label={t("project.tools")} items={asList(p.tools, lang)} />
              <HighlightCard label={t("project.locations")} items={[asText(p.locations, lang)]} />
            </div>
          </div>
        </section>
      )}

      {/* RESULT (optional bg image) */}
      {!p.hideResult && p.result && (
        <section className="relative py-20 md:py-28 overflow-hidden">
          {p.resultBackground && (
            <div className="absolute inset-0">
              <img src={p.resultBackground} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/90 via-[#0A0A0C]/85 to-[#0A0A0C]" />
            </div>
          )}
          <div className="relative container-x max-w-4xl">
            <div className="overline mb-4">{t("project.result")}</div>
            <h2 className="display-h2 italic">{asText(p.result, lang)}</h2>
          </div>
        </section>
      )}

      {/* NEXT PROJECT */}
      <a
        href={`/work/${next.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="next-project-cta"
        className="relative block group border-t border-white/10"
      >
        <div className="absolute inset-0 opacity-30 group-hover:opacity-50 transition">
          <img src={next.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A0A0C]/60" />
        </div>
        <div className="relative container-x py-24 md:py-32 flex items-center justify-between gap-6">
          <div>
            <div className="overline mb-3">{t("project.next")}</div>
            <div className="display-h2 group-hover:text-[#E6A95B] transition">{next.title[lang]}</div>
          </div>
          <ArrowUpRight className="w-10 h-10 md:w-14 md:h-14 text-white/70 group-hover:text-[#E6A95B] group-hover:translate-x-2 transition" />
        </div>
      </a>
    </div>
  );
};

const Meta = ({ label, value }) => (
  <div>
    <div className="overline mb-2">{label}</div>
    <div className="text-white text-sm md:text-base">{value}</div>
  </div>
);

const HighlightCard = ({ label, items }) => (
  <div className="p-8 rounded-xl border border-white/10 bg-[#0A0A0C]">
    <div className="overline mb-4">{label}</div>
    <ul className="space-y-2 text-white/80">
      {items.filter(Boolean).map((it, i) => (
        <li key={i} className="flex gap-2"><span className="text-[#E6A95B]">—</span> {it}</li>
      ))}
    </ul>
  </div>
);

export default ProjectDetail;
